import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  SimpleGrid,
  Spinner,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiArrowRight, FiCheck, FiX, FiPlus, FiMapPin, FiShield, FiChevronDown, FiEye, FiEdit2, FiAlertTriangle, FiMessageSquare, FiImage, FiPaperclip } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateEvent, useUpdateEvent, useEvent, useReopenEvent, eventReviewersQueryKey } from '../../hooks/useEvents';
import { useAuth } from '../../contexts/AuthContext';
import api, { fetchAttachmentObjectUrl } from '../../services/api';
import EventComplianceModal from './EventComplianceModal';
import EventCoverUploader from './EventCoverUploader';
import EventAttachmentUploader from './EventAttachmentUploader';
import {
  EVENT_FORMATS,
  EVENT_CATEGORIES,
  EVENT_CITIES,
  EVENT_DISCLOSURES,
  DISCLOSURE_ANSWER_OPTIONS,
  DISCLOSURE_YES,
  DISCLOSURE_NO,
  emptyDisclosures,
  isOnlineFormat,
  GILLYREACH_DISCLOSURE,
  SOCIAL_NETWORKS,
} from './eventConstants';
import { getVenuesForCity, getAllVenues } from './eventVenues';
import { TYPE_META } from './venueTypeMeta';
import EventPreviewCard, { ComplianceBanner } from './EventPreviewCard';

// Four steps — dates come after compliance approval, not before.
const STEPS = [
  { key: 'venue', label: 'Venue' },
  { key: 'details', label: 'Event Details' },
  { key: 'promotion', label: 'Promotion' },
  { key: 'compliance', label: 'Compliance' },
];

const STEP_VENUE = 0;
const STEP_DETAILS = 1;
const STEP_PROMOTION = 2;
const STEP_COMPLIANCE = 3;

const inputProps = {
  bg: '#171717',
  borderColor: 'rgba(255,255,255,0.12)',
  color: '#fafafa',
  fontSize: 'sm',
  _placeholder: { color: 'rgba(255,255,255,0.25)' },
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
};

const Label = ({ children, optional, hint }) => (
  <Box mb={hint ? 2.5 : 1.5}>
    <FormLabel fontSize="sm" color="#fafafa" fontWeight="600" mb={hint ? 1 : 0}>
      {children}
      {optional && (
        <Text as="span" ml={1.5} fontSize="xs" fontWeight="400" color="rgba(255,255,255,0.3)">
          (optional)
        </Text>
      )}
    </FormLabel>
    {hint && (
      <Text fontSize="xs" color="rgba(255,255,255,0.42)" lineHeight="1.6">
        {hint}
      </Text>
    )}
  </Box>
);

// Custom dropdown built on Chakra Menu — the native <select> popup is
// browser-chrome and clashes with the dark theme. Options accepts strings.
const StyledSelect = ({ value, onChange, placeholder, options }) => {
  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = normalized.find((o) => o.value === value);
  return (
    <Menu matchWidth gutter={6} isLazy>
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            variant="unstyled"
            w="100%"
            h="40px"
            px={3}
            display="flex"
            alignItems="center"
            bg="#171717"
            border="1px solid"
            borderColor={isOpen ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)'}
            borderRadius="8px"
            fontWeight="400"
            _hover={{ borderColor: 'rgba(255,255,255,0.22)' }}
            _focus={{ boxShadow: 'none' }}
            _active={{ bg: '#171717' }}
          >
            <Flex align="center" justify="space-between" w="100%">
              <Text fontSize="sm" noOfLines={1} color={selected ? '#fafafa' : 'rgba(255,255,255,0.4)'}>
                {selected ? selected.label : placeholder}
              </Text>
              <Box
                color="rgba(255,255,255,0.5)"
                ml={2}
                flexShrink={0}
                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                transition="transform 0.15s ease"
              >
                <FiChevronDown size={16} />
              </Box>
            </Flex>
          </MenuButton>
          <MenuList
            bg="#171717"
            border="1px solid rgba(255,255,255,0.12)"
            borderRadius="10px"
            py={1.5}
            px={1.5}
            minW="0"
            maxH="280px"
            overflowY="auto"
            boxShadow="0 12px 32px rgba(0,0,0,0.55)"
          >
            {normalized.map((o) => {
              const isSel = o.value === value;
              return (
                <MenuItem
                  key={o.value}
                  onClick={() => onChange(o.value)}
                  bg="transparent"
                  color={isSel ? '#fafafa' : '#d4d4d4'}
                  fontSize="sm"
                  borderRadius="7px"
                  px={2.5}
                  py={2}
                  _hover={{ bg: 'rgba(255,255,255,0.07)' }}
                  _focus={{ bg: 'rgba(255,255,255,0.07)' }}
                >
                  <Flex w="100%" align="center" justify="space-between">
                    <span>{o.label}</span>
                    {isSel && <FiCheck size={14} />}
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </>
      )}
    </Menu>
  );
};

const Segmented = ({ value, onChange, options }) => (
  <HStack
    spacing={1}
    bg="rgba(255,255,255,0.04)"
    border="1px solid rgba(255,255,255,0.10)"
    borderRadius="full"
    p="3px"
    w="fit-content"
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <Button
          key={opt.value}
          size="sm"
          h="30px"
          px={4}
          borderRadius="full"
          fontSize="sm"
          fontWeight={active ? '600' : '500'}
          bg={active ? '#fafafa' : 'transparent'}
          color={active ? '#0a0a0a' : '#a3a3a3'}
          _hover={active ? { bg: '#fafafa' } : { color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
          _focus={{ boxShadow: 'none' }}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      );
    })}
  </HStack>
);

// Amount field with a leading $ — value is kept as raw text (no parsing) so the
// attested figure is never silently reformatted before it reaches compliance.
const MoneyInput = ({ value, onChange, placeholder, locked }) => (
  <InputGroup>
    <InputLeftElement pointerEvents="none" h="40px" color="rgba(255,255,255,0.4)" fontSize="sm">
      $
    </InputLeftElement>
    <Input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      inputMode="decimal"
      pl={7}
      {...inputProps}
      {...(locked ? { isReadOnly: true, pointerEvents: 'auto' } : {})}
    />
  </InputGroup>
);

const Stepper = ({ current }) => (
  <HStack spacing={0} mb={8}>
    {STEPS.map((step, idx) => {
      const done = idx < current;
      const active = idx === current;
      return (
        <React.Fragment key={step.key}>
          <HStack spacing={2.5} flexShrink={0}>
            <Flex
              align="center"
              justify="center"
              w="26px"
              h="26px"
              borderRadius="full"
              fontSize="xs"
              fontWeight="600"
              bg={done ? '#fafafa' : active ? 'rgba(255,255,255,0.10)' : 'transparent'}
              color={done ? '#0a0a0a' : active ? '#fafafa' : 'rgba(255,255,255,0.4)'}
              border={`1px solid ${active || done ? 'transparent' : 'rgba(255,255,255,0.15)'}`}
            >
              {done ? <FiCheck size={13} /> : idx + 1}
            </Flex>
            <Text
              fontSize="sm"
              fontWeight={active ? '600' : '500'}
              color={active || done ? '#fafafa' : 'rgba(255,255,255,0.4)'}
              display={{ base: 'none', sm: 'block' }}
            >
              {step.label}
            </Text>
          </HStack>
          {idx < STEPS.length - 1 && (
            <Box flex="1" h="1px" mx={4} bg={done ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.10)'} />
          )}
        </React.Fragment>
      );
    })}
  </HStack>
);

const VenueCard = ({ venue, selected, onSelect }) => {
  const meta = TYPE_META[venue.type] || TYPE_META.Golf;
  const TypeIcon = meta.icon;
  return (
    <Box
      role="button"
      onClick={() => onSelect(venue)}
      cursor="pointer"
      bg="#171717"
      borderRadius="12px"
      overflow="hidden"
      border={`1px solid ${selected ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.10)'}`}
      boxShadow={selected ? '0 0 0 1px rgba(255,255,255,0.45)' : 'none'}
      transition="all 0.15s"
      _hover={{ borderColor: selected ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)', transform: 'translateY(-2px)' }}
    >
      <Box position="relative" h="74px" bgGradient={meta.gradient}>
        <Flex position="absolute" inset={0} align="center" justify="center" opacity={0.92}>
          <TypeIcon size={26} color="rgba(255,255,255,0.92)" />
        </Flex>
        <Box position="absolute" top={2} right={2}>
          <Box px={2} py="2px" borderRadius="full" bg="rgba(0,0,0,0.38)" border="1px solid rgba(255,255,255,0.2)">
            <Text fontSize="9px" fontWeight="700" color="#fafafa" letterSpacing="0.06em" textTransform="uppercase">
              {venue.type}
            </Text>
          </Box>
        </Box>
        {selected && (
          <Flex
            position="absolute" top={2} left={2}
            align="center" justify="center"
            w="22px" h="22px" borderRadius="full"
            bg="#fafafa" color="#0a0a0a"
          >
            <FiCheck size={14} />
          </Flex>
        )}
        {venue.city && (
          <Box position="absolute" bottom={2} left={2}>
            <Box px={2} py="2px" borderRadius="full" bg="rgba(0,0,0,0.4)" border="1px solid rgba(255,255,255,0.18)">
              <Text fontSize="10px" fontWeight="600" color="#fafafa" letterSpacing="0.02em">
                {venue.city.split(',')[0]}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
      <Box p={3.5}>
        <Text fontSize="sm" fontWeight="600" color="#fafafa" noOfLines={1} mb={1}>
          {venue.name}
        </Text>
        <HStack spacing={1.5} color="#a3a3a3" mb={1.5} align="flex-start">
          <Box mt="2px" flexShrink={0}><FiMapPin size={11} /></Box>
          <Text fontSize="xs" noOfLines={1}>{venue.address}</Text>
        </HStack>
        {venue.detail && (
          <Box mb={2}>
            <Box display="inline-block" px={2} py="1px" borderRadius="full" bg={meta.bg} border={`1px solid ${meta.border}`}>
              <Text fontSize="10px" fontWeight="600" color={meta.tint}>
                {venue.detail}
              </Text>
            </Box>
          </Box>
        )}
        <Text fontSize="xs" color="rgba(255,255,255,0.45)" noOfLines={2} lineHeight="1.5">
          {venue.blurb}
        </Text>
      </Box>
    </Box>
  );
};

const initialForm = {
  title: '',
  format: 'in_person',
  summary: '',
  category: '',
  description: '',
  expectedAttendees: '',
  visibility: 'public',
  pricingType: 'free',
  ticketPrice: '',
  city: '',
  venueId: '',
  venueName: '',
  address: '',
  onlineUrl: '',
  coverImage: '',
  complianceDisclosures: emptyDisclosures(),
  complianceAttachments: [],
  totalEventValue: '',
  complianceNotes: '',
  socialPosts: [],
  promotionFlyers: [],
  qualificationQuestions: [],
};

// Merge a persisted disclosures object onto the full empty shape so editing an
// event saved before this field existed (or with only some blocks) never throws
// on a missing key.
const hydrateDisclosures = (saved) => {
  const base = emptyDisclosures();
  if (!saved || typeof saved !== 'object') return base;
  Object.keys(base).forEach((key) => {
    const block = saved[key];
    if (block && typeof block === 'object') {
      base[key] = {
        answer: block.answer || '',
        payer: block.payer || '',
        amount: block.amount || '',
      };
    }
  });
  return base;
};

// ─── Auth-gated image (fetches blob via axios so the token is attached) ───────
const AuthedImage = ({ url, alt, ...boxProps }) => {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!url) return;
    let revoked = false;
    fetchAttachmentObjectUrl(url).then(({ objectUrl: ou }) => {
      if (!revoked) setObjectUrl(ou);
    }).catch(() => {});
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <Box as="img" src={objectUrl || ''} alt={alt || ''} {...boxProps} />
  );
};

// ─── Promotion step helpers ────────────────────────────────────────────────────
const FLYER_ACCEPT = 'image/png,image/jpeg,image/jpg,application/pdf,.png,.jpg,.jpeg,.pdf';
const MAX_FLYER_BYTES = 15 * 1024 * 1024;
const MAX_FLYERS = 10;

const isFlyerType = (file) => {
  const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
  if (allowed.includes(file?.type)) return true;
  const ext = String(file?.name || '').toLowerCase().split('.').pop();
  return ['pdf', 'png', 'jpg', 'jpeg'].includes(ext);
};

const FlyerUploader = ({ value, onChange, locked }) => {
  const toast = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const items = Array.isArray(value) ? value : [];

  const warn = (desc) =>
    toast({ title: "Couldn't add file", description: desc, status: 'warning', duration: 3200, isClosable: true });

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_FLYERS - items.length;
    if (room <= 0) { warn(`You can upload up to ${MAX_FLYERS} flyers and posters.`); return; }
    const accepted = [];
    for (const file of files.slice(0, room)) {
      if (!isFlyerType(file)) { warn(`"${file.name}" isn't supported. Use PDF, PNG, or JPG.`); continue; }
      if (file.size > MAX_FLYER_BYTES) { warn(`"${file.name}" is over 15 MB.`); continue; }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of accepted) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const { data } = await api.post('/api/event-attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          uploaded.push(data);
        } catch (err) {
          warn(err?.response?.data?.detail || `Couldn't upload "${file.name}".`);
        }
      }
      if (uploaded.length) onChange?.([...items, ...uploaded]);
    } finally {
      setUploading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, onChange]);

  const remove = (id) => onChange?.(items.filter((it) => it.id !== id));

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={FLYER_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />
      <Flex
        role="button"
        onClick={() => !locked && !uploading && inputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); if (!locked && !uploading) handleFiles(e.dataTransfer.files); }}
        onDragOver={(e) => { e.preventDefault(); if (!locked && !uploading) setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        direction="column"
        align="center"
        justify="center"
        gap={2}
        px={4}
        py={5}
        textAlign="center"
        borderRadius="12px"
        border="1px dashed"
        borderColor={dragActive ? 'rgba(99,179,237,0.55)' : 'rgba(255,255,255,0.14)'}
        bg={dragActive ? 'rgba(99,179,237,0.06)' : '#171717'}
        cursor={locked || uploading ? 'default' : 'pointer'}
        transition="all 0.15s"
        _hover={{ borderColor: locked || uploading ? undefined : 'rgba(255,255,255,0.3)' }}
      >
        <Flex w="38px" h="38px" borderRadius="full" bg="rgba(255,255,255,0.06)" border="1px solid rgba(255,255,255,0.12)" align="center" justify="center" color="rgba(255,255,255,0.6)">
          {uploading ? <Spinner size="sm" /> : <FiPaperclip size={16} />}
        </Flex>
        <Text fontSize="sm" fontWeight="600" color="#fafafa">
          {uploading ? 'Uploading…' : 'Add flyers and posters'}
        </Text>
        <Text fontSize="xs" color="rgba(255,255,255,0.4)">
          Drag & drop or click · PDF, PNG, JPG · up to 15 MB each
        </Text>
      </Flex>

      {items.length > 0 && (
        <VStack align="stretch" spacing={2} mt={3}>
          {items.map((item) => (
            <HStack
              key={item.id}
              spacing={3}
              px={3}
              py={2.5}
              borderRadius="10px"
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.08)"
            >
              <Box color="rgba(255,255,255,0.5)" flexShrink={0} display="flex" alignItems="center">
                <FiPaperclip size={15} />
              </Box>
              <Box flex={1} minW={0}>
                <Text fontSize="sm" color="#fafafa" noOfLines={1}>{item.name}</Text>
                {item.size ? <Text fontSize="xs" color="rgba(255,255,255,0.38)">{(item.size / (1024 * 1024)).toFixed(1)} MB</Text> : null}
              </Box>
              {!locked && (
                <Box
                  as="button"
                  onClick={() => remove(item.id)}
                  color="rgba(255,255,255,0.3)"
                  _hover={{ color: 'rgba(248,113,113,0.85)' }}
                  display="flex"
                  alignItems="center"
                  transition="color 0.15s"
                >
                  <FiX size={15} />
                </Box>
              )}
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
};

const SocialPostCard = ({ post, onUpdate, onRemove, locked }) => {
  const toast = useToast();
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type) && !['png','jpg','jpeg','gif','webp'].includes(file.name.split('.').pop()?.toLowerCase())) {
      toast({ title: 'Images only', description: 'Upload a PNG, JPG, GIF, or WebP.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (file.size > MAX_FLYER_BYTES) {
      toast({ title: 'File too large', description: 'Image must be under 15 MB.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/event-attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUpdate('photo', data);
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.', status: 'error', duration: 3200, isClosable: true });
    } finally {
      setUploadingPhoto(false);
    }
  }, [onUpdate, toast]);

  return (
    <Box
      p={4}
      bg="rgba(255,255,255,0.02)"
      border="1px solid rgba(255,255,255,0.08)"
      borderRadius="12px"
    >
      {/* Header row */}
      <Flex align="center" justify="space-between" mb={4}>
        <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.6)">Social post</Text>
        {!locked && (
          <Box
            as="button"
            onClick={onRemove}
            color="rgba(255,255,255,0.3)"
            _hover={{ color: 'rgba(248,113,113,0.85)' }}
            display="flex"
            alignItems="center"
            transition="color 0.15s"
          >
            <FiX size={16} />
          </Box>
        )}
      </Flex>

      {/* Network selector — multi-select */}
      <Box mb={4}>
        <Text fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.55)" mb={2} textTransform="uppercase" letterSpacing="0.04em">
          Networks
        </Text>
        <HStack spacing={1.5} flexWrap="wrap" rowGap={1.5}>
          {SOCIAL_NETWORKS.map((n) => {
            const selected = Array.isArray(post.networks) ? post.networks : [];
            const active = selected.includes(n.value);
            const toggle = () => {
              if (locked) return;
              const next = active
                ? selected.filter((v) => v !== n.value)
                : [...selected, n.value];
              onUpdate('networks', next);
            };
            return (
              <Button
                key={n.value}
                size="sm"
                h="30px"
                px={3.5}
                borderRadius="full"
                fontSize="sm"
                fontWeight={active ? '600' : '500'}
                bg={active ? '#fafafa' : 'transparent'}
                color={active ? '#0a0a0a' : '#a3a3a3'}
                border="1px solid"
                borderColor={active ? 'transparent' : 'rgba(255,255,255,0.12)'}
                _hover={active ? { bg: '#fafafa' } : { color: '#fafafa', borderColor: 'rgba(255,255,255,0.25)' }}
                _focus={{ boxShadow: 'none' }}
                onClick={toggle}
                isDisabled={locked}
              >
                {n.label}
              </Button>
            );
          })}
        </HStack>
      </Box>

      {/* Post preview — image + caption laid out like a social post */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp"
        style={{ display: 'none' }}
        onChange={(e) => { handlePhotoFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      <Box
        borderRadius="12px"
        overflow="hidden"
        border="1px solid rgba(255,255,255,0.10)"
        bg="rgba(255,255,255,0.02)"
      >
        {/* Image area */}
        {post.photo ? (
          <Box position="relative">
            <AuthedImage
              url={post.photo.url}
              alt="Post photo"
              w="100%"
              display="block"
              maxH="360px"
              objectFit="contain"
              bg="#0a0a0a"
            />
            {/* Replace / remove controls */}
            {!locked && (
              <HStack position="absolute" top={2} right={2} spacing={1.5}>
                <Box
                  as="button"
                  onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  bg="rgba(0,0,0,0.65)"
                  border="1px solid rgba(255,255,255,0.18)"
                  _hover={{ bg: 'rgba(0,0,0,0.85)' }}
                  display="flex"
                  alignItems="center"
                  gap="6px"
                  transition="background 0.15s"
                >
                  <FiImage size={12} color="#fafafa" />
                  <Text fontSize="11px" fontWeight="600" color="#fafafa">Replace</Text>
                </Box>
                <Box
                  as="button"
                  onClick={() => onUpdate('photo', null)}
                  p={1.5}
                  borderRadius="full"
                  bg="rgba(0,0,0,0.65)"
                  border="1px solid rgba(255,255,255,0.18)"
                  _hover={{ bg: 'rgba(239,68,68,0.55)' }}
                  display="flex"
                  alignItems="center"
                  transition="background 0.15s"
                >
                  <FiX size={13} color="#fafafa" />
                </Box>
              </HStack>
            )}
          </Box>
        ) : (
          <Flex
            role="button"
            onClick={() => !locked && !uploadingPhoto && photoInputRef.current?.click()}
            direction="column"
            align="center"
            justify="center"
            gap={2}
            px={4}
            py={10}
            textAlign="center"
            bg="#0f0f0f"
            cursor={locked || uploadingPhoto ? 'default' : 'pointer'}
            _hover={{ bg: locked || uploadingPhoto ? '#0f0f0f' : 'rgba(255,255,255,0.03)' }}
            transition="background 0.15s"
          >
            {uploadingPhoto ? (
              <Spinner size="md" color="rgba(255,255,255,0.4)" thickness="2px" />
            ) : (
              <Box color="rgba(255,255,255,0.2)"><FiImage size={28} /></Box>
            )}
            <Text fontSize="sm" color="rgba(255,255,255,0.35)" fontWeight="500">
              {uploadingPhoto ? 'Uploading…' : 'Click to add a photo'}
            </Text>
            <Text fontSize="xs" color="rgba(255,255,255,0.2)">PNG, JPG, GIF, WebP · up to 15 MB</Text>
          </Flex>
        )}

        {/* Caption */}
        <Box px={3} py={3} borderTop={post.photo ? '1px solid rgba(255,255,255,0.07)' : 'none'}>
          <Textarea
            value={post.caption}
            onChange={(e) => onUpdate('caption', e.target.value)}
            placeholder="Write your post caption here…"
            bg="transparent"
            border="none"
            color="#fafafa"
            fontSize="sm"
            rows={3}
            resize="none"
            isReadOnly={locked}
            _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
            _hover={{}}
            _focus={{ boxShadow: 'none' }}
            sx={{ caretColor: '#fafafa', p: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const CreateEventWizard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { eventId } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent();
  const reopenMutation = useReopenEvent();
  const { data: existingEvent } = useEvent(eventId);
  const isEditing = Boolean(eventId);
  // Content is frozen once it's under (or past) review: a reviewer signs off on
  // exactly what they saw. pending → awaiting verdict; approved → final. The
  // backend enforces this too (PATCH returns 409); this is the matching UX.
  const lockStatus = isEditing ? existingEvent?.complianceStatus : null;
  const isLockedForReview = lockStatus === 'pending' || lockStatus === 'approved';
  // Pending events can be pulled back to edit (approved is final). Reopening
  // revokes the reviewer's link and reverts to a draft — they edit + resubmit.
  const canReopenForEdit = lockStatus === 'pending';
  // A rejected event comes back to the advisor to edit. The reviewer's note
  // (joined from the share on the owner's event read) is the action-driving
  // context, so we surface it as a sticky banner + on-demand drawer.
  const reviewerNote = existingEvent?.reviewerNote || '';
  const isRejected = lockStatus === 'rejected';
  const showRejectionNotice = isRejected && Boolean(reviewerNote.trim());
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const descriptionRef = useRef(null);
  const descriptionCallbackRef = useCallback((el) => {
    descriptionRef.current = el;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  const [customVenue, setCustomVenue] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showReviewerNote, setShowReviewerNote] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  // Holds the event id to invite a reviewer for once the form is submitted.
  const [submittedEventId, setSubmittedEventId] = useState(null);

  // Prefill the form when editing an existing event (data arrives async).
  useEffect(() => {
    if (!eventId || !existingEvent) return;
    setForm((prev) => ({
      ...prev,
      title: existingEvent.title || '',
      format: existingEvent.format || 'in_person',
      summary: existingEvent.summary || '',
      category: existingEvent.category || '',
      description: existingEvent.description
        ? existingEvent.description.replace(`\n\n---\n${GILLYREACH_DISCLOSURE}`, '').trimEnd()
        : '',
      expectedAttendees: existingEvent.expectedAttendees || '',
      visibility: existingEvent.visibility || 'public',
      pricingType: existingEvent.pricingType || 'free',
      ticketPrice: existingEvent.ticketPrice || '',
      city: existingEvent.city || '',
      venueId: existingEvent.venueId || '',
      venueName: existingEvent.venueName || '',
      address: existingEvent.address || '',
      onlineUrl: existingEvent.onlineUrl || '',
      coverImage: existingEvent.coverImage || '',
      complianceDisclosures: hydrateDisclosures(existingEvent.complianceDisclosures),
      complianceAttachments: Array.isArray(existingEvent.complianceAttachments)
        ? existingEvent.complianceAttachments
        : [],
      totalEventValue: existingEvent.totalEventValue || '',
      complianceNotes: existingEvent.complianceNotes || '',
      socialPosts: Array.isArray(existingEvent.socialPosts)
        ? existingEvent.socialPosts.map((p) => ({
            ...p,
            // Migrate old single-network string to array
            networks: Array.isArray(p.networks) ? p.networks : (p.network ? [p.network] : []),
          }))
        : [],
      promotionFlyers: Array.isArray(existingEvent.promotionFlyers) ? existingEvent.promotionFlyers : [],
      qualificationQuestions: Array.isArray(existingEvent.qualificationQuestions) ? existingEvent.qualificationQuestions : [],
    }));
    if (existingEvent.venueName && !existingEvent.venueId) setCustomVenue(existingEvent.venueName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, existingEvent]);

  // Auto-resize the description textarea to fit its content.
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [form.description]);

  // Warm the reviewer list before the submit modal opens, so picking a reviewer
  // is instant instead of waiting on a fetch off the critical path.
  useEffect(() => {
    if (step !== STEP_COMPLIANCE || !user?.id) return;
    queryClient.prefetchQuery({
      queryKey: eventReviewersQueryKey(user.id),
      queryFn: async () => {
        const { data } = await api.get('/api/event-compliance/reviewers');
        return data;
      },
      staleTime: 5 * 60 * 1000,
    });
  }, [step, user?.id, queryClient]);

  const set = (field) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Setter for a single disclosure field, e.g. setDisclosure('alcohol', 'amount').
  // Flipping a gate to "No" clears its follow-ups so the record can never carry
  // a payer/amount that contradicts the answer.
  const setDisclosure = (blockKey, field) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((prev) => {
      const prevBlock = prev.complianceDisclosures[blockKey];
      const nextBlock = { ...prevBlock, [field]: value };
      if (field === 'answer' && value !== DISCLOSURE_YES) {
        nextBlock.payer = '';
        nextBlock.amount = '';
      }
      return {
        ...prev,
        complianceDisclosures: { ...prev.complianceDisclosures, [blockKey]: nextBlock },
      };
    });
  };

  const handleCityChange = (city) => {
    setForm((prev) => ({ ...prev, city, venueId: '', venueName: '', address: '' }));
    setCustomVenue('');
  };

  const selectVenue = (venue) => {
    setForm((prev) =>
      prev.venueId === venue.id
        ? { ...prev, venueId: '', venueName: '', address: '' }
        : {
            ...prev,
            // Picking a venue from the all-cities list also locks in its city.
            city: venue.city || prev.city,
            venueId: venue.id,
            venueName: venue.name,
            address: venue.address,
          }
    );
    setCustomVenue('');
  };

  const handleCustomVenueChange = (e) => {
    setCustomVenue(e.target.value);
    // Deselect card if typing a custom venue
    if (form.venueId) {
      setForm((prev) => ({ ...prev, venueId: '', venueName: '', address: '' }));
    }
  };

  const venueOptions = form.city ? getVenuesForCity(form.city) : getAllVenues();
  const selectedVenue = venueOptions.find((v) => v.id === form.venueId);
  const needsOnline = isOnlineFormat(form.format);

  // ── Social post helpers ────────────────────────────────────────────────────
  const addSocialPost = () => {
    setForm((prev) => ({
      ...prev,
      socialPosts: [
        ...prev.socialPosts,
        { _id: `${Date.now()}-${Math.random()}`, networks: [], caption: '', photo: null },
      ],
    }));
  };

  const updateSocialPost = useCallback((id, field, value) => {
    setForm((prev) => ({
      ...prev,
      socialPosts: prev.socialPosts.map((p) => (p._id === id ? { ...p, [field]: value } : p)),
    }));
  }, []);

  const removeSocialPost = useCallback((id) => {
    setForm((prev) => ({
      ...prev,
      socialPosts: prev.socialPosts.filter((p) => p._id !== id),
    }));
  }, []);

  // Resolved venue name for payload: card pick wins, then manual entry.
  const resolvedVenueName = form.venueName || customVenue.trim() || null;

  const warn = (description) =>
    toast({ title: 'Missing details', description, status: 'warning', duration: 2800, isClosable: true });

  const validateStep = (idx) => {
    if (idx === STEP_VENUE) {
      if (!form.city) return warn('Select the city for this event.') && false;
      return true;
    }
    if (idx === STEP_DETAILS) {
      if (!form.title.trim()) return warn('Add an event title.') && false;
      if (!form.description.trim()) return warn('Add a description — this is what compliance will review.') && false;
      if (form.format === 'online' && !form.onlineUrl.trim())
        return warn('Add the online meeting link.') && false;
      if (form.pricingType === 'paid' && !(Number(form.ticketPrice) > 0))
        return warn('Add a ticket price for this paid event.') && false;
      return true;
    }
    if (idx === STEP_COMPLIANCE) {
      for (const block of EVENT_DISCLOSURES) {
        const value = form.complianceDisclosures[block.key] || {};
        if (value.answer !== DISCLOSURE_YES && value.answer !== DISCLOSURE_NO)
          return warn(`Answer: "${block.gateLabel}"`) && false;
        if (value.answer === DISCLOSURE_YES) {
          if (!value.payer.trim()) return warn(`Add a response: "${block.payerLabel}"`) && false;
          if (!block.noAmount && !value.amount.trim()) return warn(`Add an amount: "${block.amountLabel}"`) && false;
        }
      }
      if (form.pricingType === 'free' && !form.totalEventValue.trim())
        return warn('Add the total dollar value of this event.') && false;
      return true;
    }
    return true;
  };

  const goNext = () => {
    // While locked the form is read-only, so let the advisor page through to
    // review what they submitted without tripping field validation.
    if (!isLockedForReview && !validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // Content-only payload. The backend owns status / compliance_status — they
  // transition via the invite + verdict endpoints, never from a content edit.
  const buildPayload = () => ({
    title: form.title.trim(),
    format: form.format,
    summary: form.summary.trim() || null,
    category: form.category || null,
    description: form.description.trim()
      ? `${form.description.trim()}\n\n---\n${GILLYREACH_DISCLOSURE}`
      : null,
    expectedAttendees: form.expectedAttendees || null,
    visibility: form.visibility,
    pricingType: form.pricingType,
    ticketPrice: form.pricingType === 'paid' ? form.ticketPrice || null : null,
    city: form.city || null,
    venueId: form.venueId || null,
    venueName: resolvedVenueName,
    venueType: selectedVenue?.type || null,
    address: form.address || null,
    onlineUrl: needsOnline ? form.onlineUrl.trim() || null : null,
    coverImage: form.coverImage || null,
    complianceDisclosures: buildDisclosuresPayload(),
    complianceAttachments: Array.isArray(form.complianceAttachments) ? form.complianceAttachments : [],
    totalEventValue: form.pricingType === 'free' ? form.totalEventValue.trim() || null : null,
    complianceNotes: form.complianceNotes.trim() || null,
    socialPosts: form.socialPosts,
    promotionFlyers: Array.isArray(form.promotionFlyers) ? form.promotionFlyers : [],
    qualificationQuestions: (form.qualificationQuestions || []).filter((q) => q.trim()),
    // No start/end — dates are confirmed only after compliance approves.
    start: null,
    end: null,
  });

  // Normalize disclosures for persistence: an answer must be yes/no/null, and a
  // non-"yes" answer carries no payer/amount so the stored record is internally
  // consistent (matches the backend's contract and the audit expectation).
  const buildDisclosuresPayload = () =>
    EVENT_DISCLOSURES.reduce((acc, block) => {
      const value = form.complianceDisclosures[block.key] || {};
      const answer = value.answer === DISCLOSURE_YES || value.answer === DISCLOSURE_NO ? value.answer : null;
      acc[block.key] = {
        answer,
        payer: answer === DISCLOSURE_YES ? value.payer.trim() || null : null,
        amount: answer === DISCLOSURE_YES ? value.amount.trim() || null : null,
      };
      return acc;
    }, {});

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const saveError = (err) =>
    toast({
      title: "Couldn't save event",
      description: err?.response?.data?.detail || err?.message || 'Please try again.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  // Persist the event (create or update), then open the invite modal so the
  // advisor can send it to a reviewer. Inviting is what flips the event to
  // "pending review" on the backend.
  const handleSubmitForReview = async () => {
    if (isLockedForReview) return;
    for (let i = 0; i < STEPS.length; i += 1) {
      if (!validateStep(i)) { setStep(i); return; }
    }
    const payload = buildPayload();
    try {
      let id = eventId;
      if (isEditing) {
        await updateMutation.mutateAsync({ id: eventId, patch: payload });
      } else {
        const created = await createMutation.mutateAsync(payload);
        id = created.id;
      }
      setSubmittedEventId(id);
    } catch (err) {
      saveError(err);
    }
  };

  const handleSaveDraft = async () => {
    if (isLockedForReview) return;
    if (!form.city && !form.title.trim()) {
      warn('Add at least a city before saving a draft.');
      return;
    }
    const payload = buildPayload();
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id: eventId, patch: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      toast({ title: 'Draft saved', status: 'success', duration: 2500, isClosable: true });
      navigate('/?view=events');
    } catch (err) {
      saveError(err);
    }
  };

  // Reopen a pending event for editing. The server revokes the reviewer's link
  // (and emails them), then reverts to a draft — which lifts the lock here once
  // the event refetches. The advisor edits and resubmits to issue a fresh link.
  const handleReopenForEdit = async () => {
    if (!canReopenForEdit || reopenMutation.isLoading) return;
    try {
      await reopenMutation.mutateAsync(eventId);
      setShowReopenConfirm(false);
      toast({
        title: 'Reopened for editing',
        description: "Your reviewer has been notified their link no longer works. Resubmit when you're done.",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      saveError(err);
    }
  };

  const isLastStep = step === STEPS.length - 1;

  const stepContent = useMemo(() => {
    // When locked, the wrapper sets `pointer-events: none` on everything (so no
    // venue cards / selects / uploads are interactive). Text fields opt back IN
    // to pointer events but as read-only — so the advisor can scroll long copy
    // and select/copy their own answers while review is in progress, but can't
    // edit. Spread AFTER {...inputProps} so it wins.
    const ro = isLockedForReview
      ? { isReadOnly: true, pointerEvents: 'auto' }
      : null;
    // ── Step 1: Venue ──────────────────────────────────────────────────
    if (step === 0) {
      return (
        <VStack spacing={6} align="stretch">
          <FormControl isRequired>
            <Label>City</Label>
            <StyledSelect
              value={form.city}
              onChange={handleCityChange}
              placeholder="Where is this event?"
              options={EVENT_CITIES}
            />
          </FormControl>

          <Box>
            <Flex align="baseline" justify="space-between" mb={3}>
              <Text fontSize="sm" fontWeight="600" color="#fafafa">Select a venue</Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                {form.city ? `Curated venues near ${form.city.split(',')[0]}` : 'Browse venues across all cities'}
              </Text>
            </Flex>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3} mb={4}>
              {venueOptions.map((v) => (
                <VenueCard key={v.id} venue={v} selected={form.venueId === v.id} onSelect={selectVenue} />
              ))}
            </SimpleGrid>

            {/* Manual override */}
            <Box pt={4} borderTop="1px solid rgba(255,255,255,0.07)">
              <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>
                Different venue in mind?
              </Text>
              <Input
                value={customVenue}
                onChange={handleCustomVenueChange}
                placeholder="Type a venue name…"
                {...inputProps}
                {...ro}
              />
            </Box>
          </Box>
        </VStack>
      );
    }

    // ── Step 3: Promotion ──────────────────────────────────────────────
    if (step === STEP_PROMOTION) {
      return (
        <VStack spacing={6} align="stretch">
          {/* Info banner */}
          <Box
            p={4}
            bg="rgba(255,255,255,0.03)"
            border="1px solid rgba(255,255,255,0.08)"
            borderRadius="10px"
          >
            <Text fontSize="xs" color="rgba(255,255,255,0.45)" lineHeight="1.7">
              All fields on this step are optional. Add social posts, flyers, and posters you plan to use to
              promote this event — they are not reviewed for compliance but are stored with the event
              record for your reference.
            </Text>
          </Box>

          {/* Social posts */}
          <Box>
            <Box mb={3}>
              <Text fontSize="sm" fontWeight="600" color="#fafafa">Social posts</Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" mt={0.5}>
                Add a post per network. Each post can have a photo and caption.
              </Text>
            </Box>

            <VStack spacing={3} align="stretch">
              {form.socialPosts.map((post) => (
                <SocialPostCard
                  key={post._id}
                  post={post}
                  onUpdate={(field, value) => updateSocialPost(post._id, field, value)}
                  onRemove={() => removeSocialPost(post._id)}
                  locked={isLockedForReview}
                />
              ))}
            </VStack>

            {!isLockedForReview && (
              <Button
                mt={form.socialPosts.length > 0 ? 3 : 0}
                size="sm"
                variant="ghost"
                leftIcon={<FiMessageSquare size={14} />}
                color="rgba(255,255,255,0.55)"
                border="1px dashed rgba(255,255,255,0.14)"
                borderRadius="8px"
                w="100%"
                h="38px"
                fontWeight="500"
                _hover={{ color: '#fafafa', borderColor: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.03)' }}
                _focus={{ boxShadow: 'none' }}
                onClick={addSocialPost}
              >
                Add social post
              </Button>
            )}
          </Box>

          {/* Flyers */}
          <Box>
            <Box mb={3}>
              <Text fontSize="sm" fontWeight="600" color="#fafafa">Flyers and posters</Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" mt={0.5}>
                Upload flyers or posters you plan to distribute — PDF or image, up to 15 MB each.
              </Text>
            </Box>
            <FlyerUploader
              value={form.promotionFlyers}
              onChange={set('promotionFlyers')}
              locked={isLockedForReview}
            />
          </Box>
        </VStack>
      );
    }

    // ── Step 4: Compliance disclosures ─────────────────────────────────
    if (step === STEP_COMPLIANCE) {
      return (
        <VStack spacing={6} align="stretch">
          <Box
            p={4}
            bg="rgba(99,179,237,0.07)"
            border="1px solid rgba(99,179,237,0.20)"
            borderRadius="10px"
          >
            <HStack spacing={2} mb={1.5}>
              <Box color="rgba(99,179,237,0.85)" flexShrink={0} display="flex" alignItems="center">
                <FiShield size={14} />
              </Box>
              <Text fontSize="sm" fontWeight="600" color="rgba(99,179,237,0.95)">
                Compliance disclosures
              </Text>
            </HStack>
            <Text fontSize="xs" color="rgba(255,255,255,0.5)" lineHeight="1.7" pl="22px">
              These questions support FINRA and SEC gifts, entertainment, and conflict-of-interest
              record-keeping. Your answers are kept on the event record and shared only with your
              compliance reviewer — they never appear on the attendee event page.
            </Text>
          </Box>

          {EVENT_DISCLOSURES.map((block) => {
            const value = form.complianceDisclosures[block.key];
            const isYes = value.answer === DISCLOSURE_YES;
            return (
              <Box
                key={block.key}
                p={5}
                bg="rgba(255,255,255,0.02)"
                border="1px solid rgba(255,255,255,0.08)"
                borderRadius="12px"
              >
                <FormControl isRequired>
                  <Label hint={block.gateHint}>{block.gateLabel}</Label>
                  <Segmented
                    value={value.answer}
                    onChange={setDisclosure(block.key, 'answer')}
                    options={DISCLOSURE_ANSWER_OPTIONS}
                  />
                </FormControl>

                {isYes && (
                  <SimpleGrid columns={{ base: 1, md: block.noAmount ? 1 : 2 }} spacing={5} mt={5}>
                    <FormControl isRequired>
                      <Label>{block.payerLabel}</Label>
                      <Input
                        value={value.payer}
                        onChange={setDisclosure(block.key, 'payer')}
                        placeholder={block.payerPlaceholder}
                        {...inputProps}
                        {...ro}
                      />
                    </FormControl>
                    {!block.noAmount && (
                      <FormControl isRequired>
                        <Label>{block.amountLabel}</Label>
                        <MoneyInput
                          value={value.amount}
                          onChange={setDisclosure(block.key, 'amount')}
                          placeholder={block.amountPlaceholder}
                          locked={isLockedForReview}
                        />
                      </FormControl>
                    )}
                  </SimpleGrid>
                )}
              </Box>
            );
          })}

          {form.pricingType === 'free' && (
            <Box
              p={5}
              bg="rgba(255,255,255,0.02)"
              border="1px solid rgba(255,255,255,0.08)"
              borderRadius="12px"
            >
              <FormControl isRequired>
                <Label hint="Include venue, catering, materials, speaker fees, and any other costs associated with this event.">
                  Total dollar value of event
                </Label>
                <MoneyInput
                  value={form.totalEventValue}
                  onChange={set('totalEventValue')}
                  placeholder="e.g. 2,500"
                  locked={isLockedForReview}
                />
              </FormControl>
            </Box>
          )}

          <Box
            p={5}
            bg="rgba(255,255,255,0.02)"
            border="1px solid rgba(255,255,255,0.08)"
            borderRadius="12px"
          >
            <FormControl>
              <Label
                optional
                hint="Any context that will help your compliance reviewer understand or approve this event faster."
              >
                Anything else compliance should know
              </Label>
              <Textarea
                value={form.complianceNotes}
                onChange={set('complianceNotes')}
                placeholder="e.g. This is an annual client appreciation event. All attendees are existing clients."
                bg="#171717"
                border="1px solid rgba(255,255,255,0.12)"
                borderRadius="8px"
                color="#fafafa"
                fontSize="sm"
                rows={4}
                resize="none"
                _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                _hover={{ borderColor: 'rgba(255,255,255,0.22)' }}
                _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                sx={{ caretColor: '#fafafa' }}
                {...ro}
              />
            </FormControl>
          </Box>

          {/* Event materials — presentations, pamphlets, handouts the reviewer
              must see. Stored on the event and surfaced in the reviewer view. */}
          <Box
            p={5}
            bg="rgba(255,255,255,0.02)"
            border="1px solid rgba(255,255,255,0.08)"
            borderRadius="12px"
          >
            <Label
              optional
              hint="Upload any presentation decks, pamphlets, or handouts you'll use at this event. Your compliance reviewer reviews every piece of marketing material before approval."
            >
              Event materials
            </Label>
            <EventAttachmentUploader
              value={form.complianceAttachments}
              onChange={set('complianceAttachments')}
            />
          </Box>
        </VStack>
      );
    }

    // ── Step 2: Event Details ──────────────────────────────────────────
    return (
      <VStack spacing={5} align="stretch">
        <Box
          p={4}
          bg="rgba(99,179,237,0.07)"
          border="1px solid rgba(99,179,237,0.20)"
          borderRadius="10px"
        >
          <HStack spacing={2} mb={1.5}>
            <Box color="rgba(99,179,237,0.85)" flexShrink={0} display="flex" alignItems="center">
              <FiShield size={14} />
            </Box>
            <Text fontSize="sm" fontWeight="600" color="rgba(99,179,237,0.95)">
              This information is used to promote your event
            </Text>
          </HStack>
          <Text fontSize="xs" color="rgba(255,255,255,0.5)" lineHeight="1.7" pl="22px">
            The details you provide here, including your title, description, cover photo, pricing, and venue, are used exactly as entered to promote this event on public event registration platforms. Promotion does not begin until your compliance reviewer approves this event.
          </Text>
        </Box>

        <FormControl>
          <Label optional hint="Shown at the top of your event page and on the event card.">
            Cover photo
          </Label>
          <EventCoverUploader value={form.coverImage} onChange={set('coverImage')} />
        </FormControl>

        <FormControl isRequired>
          <Label hint="Be clear and descriptive with a title that tells people what your event is about.">
            Event title
          </Label>
          <Input
            value={form.title}
            onChange={set('title')}
            placeholder="e.g. Advisor Golf Outing & Retirement Planning"
            {...inputProps}
            {...ro}
          />
        </FormControl>

        <FormControl>
          <Label
            optional
            hint="Grab people's attention with a short description. Attendees will see this at the top of your event page."
          >
            Summary
          </Label>
          <Textarea
            value={form.summary}
            onChange={set('summary')}
            placeholder="e.g. An intimate evening of golf and retirement-planning insights for valued clients."
            rows={2}
            maxLength={140}
            resize="none"
            {...inputProps}
            {...ro}
          />
          <Flex justify="flex-end" mt={1.5}>
            <Text fontSize="xs" color="rgba(255,255,255,0.3)">
              {form.summary.length} / 140
            </Text>
          </Flex>
        </FormControl>

        <FormControl isRequired>
          <Label>Format</Label>
          <Segmented value={form.format} onChange={set('format')} options={EVENT_FORMATS} />
        </FormControl>

        {needsOnline && (
          <FormControl isRequired={form.format === 'online'}>
            <Label optional={form.format === 'hybrid'}>Online meeting link</Label>
            <Input value={form.onlineUrl} onChange={set('onlineUrl')} placeholder="https://meet.google.com/…" {...inputProps} {...ro} />
          </FormControl>
        )}

        <FormControl isRequired>
          <Label hint="Tell attendees more about the event — the agenda, who should attend, and what they'll take away.">
            Event description
          </Label>

          {/* Unified document container — advisor text + appended disclosure as one body */}
          <Box
            border="1px solid rgba(255,255,255,0.12)"
            borderRadius="8px"
            overflow="hidden"
            _focusWithin={{ borderColor: 'rgba(255,255,255,0.35)' }}
            transition="border-color 0.15s"
          >
            {/* Editable portion */}
            <Textarea
              ref={descriptionCallbackRef}
              value={form.description}
              onChange={set('description')}
              placeholder="Describe the event, agenda, and what attendees can expect. This copy will be reviewed by compliance before the event can be scheduled."
              rows={5}
              bg="#171717"
              border="none"
              borderRadius="0"
              color="#fafafa"
              fontSize="sm"
              resize="none"
              overflow="hidden"
              _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
              _hover={{}}
              _focus={{ boxShadow: 'none' }}
              sx={{ caretColor: '#fafafa' }}
              {...ro}
            />

            {/* Seam — makes it clear the disclosure continues the same document */}
            <Flex
              align="center"
              gap={2}
              px={3}
              py={1.5}
              bg="rgba(251,191,36,0.05)"
              borderTop="1px dashed rgba(251,191,36,0.25)"
              borderBottom="1px dashed rgba(251,191,36,0.25)"
            >
              <Box color="rgba(251,191,36,0.6)" display="flex" alignItems="center" flexShrink={0}>
                <FiShield size={10} />
              </Box>
              <Text fontSize="10px" fontWeight="600" color="rgba(251,191,36,0.6)" letterSpacing="0.05em" textTransform="uppercase">
                The following disclosure is automatically appended
              </Text>
            </Flex>

            {/* Locked disclosure — same document, not editable */}
            <Box px={3} py={3} bg="rgba(255,255,255,0.02)">
              <Text fontSize="xs" color="rgba(255,255,255,0.32)" lineHeight="1.8" whiteSpace="pre-wrap" userSelect="none">
                {GILLYREACH_DISCLOSURE}
              </Text>
            </Box>
          </Box>

          <Text fontSize="xs" color="rgba(255,255,255,0.28)" mt={1.5} lineHeight="1.6">
            This is the marketing material your compliance team will review.
          </Text>
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <FormControl>
            <Label optional>Category</Label>
            <StyledSelect
              value={form.category}
              onChange={set('category')}
              placeholder="Select a category"
              options={EVENT_CATEGORIES}
            />
          </FormControl>
          <FormControl>
            <Label optional>Expected attendees</Label>
            <Input type="number" min="1" value={form.expectedAttendees} onChange={set('expectedAttendees')} placeholder="e.g. 40" {...inputProps} {...ro} />
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <Label>Pricing</Label>
          <Segmented
            value={form.pricingType}
            onChange={set('pricingType')}
            options={[
              { value: 'free', label: 'Free' },
              { value: 'paid', label: 'Paid' },
            ]}
          />
        </FormControl>

        {form.pricingType === 'paid' && (
          <FormControl isRequired>
            <Label hint="Amount each attendee pays to register.">Ticket price (USD)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.ticketPrice}
              onChange={set('ticketPrice')}
              placeholder="e.g. 50"
              {...inputProps}
              {...ro}
            />
          </FormControl>
        )}

        <FormControl>
          <Label>Visibility</Label>
          <Segmented
            value={form.visibility}
            onChange={set('visibility')}
            options={[
              { value: 'public', label: 'Public' },
              { value: 'private', label: 'Private' },
            ]}
          />
        </FormControl>

        <Box>
          <Label
            optional
            hint="Questions registrants will be asked when they sign up. Useful for dietary restrictions, company name, goals, etc."
          >
            Qualification questions
          </Label>
          <VStack spacing={2} align="stretch" mb={form.qualificationQuestions.length > 0 ? 2 : 0}>
            {form.qualificationQuestions.map((q, i) => (
              <HStack key={i} spacing={2}>
                <Input
                  value={q}
                  onChange={(e) => {
                    const next = [...form.qualificationQuestions];
                    next[i] = e.target.value;
                    setForm((f) => ({ ...f, qualificationQuestions: next }));
                  }}
                  placeholder={`Question ${i + 1}`}
                  {...inputProps}
                  {...ro}
                />
                {!isLockedForReview && (
                  <IconButton
                    icon={<FiX size={14} />}
                    size="sm"
                    variant="ghost"
                    color="rgba(255,255,255,0.4)"
                    borderRadius="full"
                    aria-label="Remove question"
                    _hover={{ color: 'rgba(248,113,113,0.9)', bg: 'rgba(248,113,113,0.08)' }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        qualificationQuestions: f.qualificationQuestions.filter((_, j) => j !== i),
                      }))
                    }
                  />
                )}
              </HStack>
            ))}
          </VStack>
          {!isLockedForReview && form.qualificationQuestions.length < 20 && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FiPlus size={14} />}
              color="rgba(255,255,255,0.5)"
              borderRadius="full"
              fontSize="sm"
              border="1px dashed rgba(255,255,255,0.14)"
              _hover={{ color: '#fafafa', borderColor: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() =>
                setForm((f) => ({ ...f, qualificationQuestions: [...f.qualificationQuestions, ''] }))
              }
            >
              Add question
            </Button>
          )}
        </Box>

      </VStack>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form, venueOptions, customVenue, needsOnline, isLockedForReview]);

  return (
    <Box maxW="780px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={1}>
        <Heading size="lg" color="#fafafa" fontWeight="700" letterSpacing="-0.02em">
          {isEditing ? 'Edit Event' : 'Create Event'}
        </Heading>
        <HStack spacing={2}>
          {step === STEP_DETAILS && (
            <Button
              leftIcon={<FiEye size={15} />}
              variant="ghost"
              size="sm"
              color="#fafafa"
              borderRadius="full"
              fontSize="sm"
              border="1px solid rgba(255,255,255,0.12)"
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => setShowPreview(true)}
            >
              Preview
            </Button>
          )}
          {canReopenForEdit && (
            <Button
              leftIcon={<FiEdit2 size={14} />}
              variant="ghost"
              size="sm"
              color="rgba(251,146,60,0.95)"
              borderRadius="full"
              fontSize="sm"
              border="1px solid rgba(251,146,60,0.3)"
              _hover={{ bg: 'rgba(251,146,60,0.1)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => setShowReopenConfirm(true)}
            >
              Edit
            </Button>
          )}
          <Button
            leftIcon={<FiX size={15} />}
            variant="ghost"
            size="sm"
            color="#a3a3a3"
            borderRadius="full"
            fontSize="sm"
            _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={() => navigate('/?view=events')}
          >
            Cancel
          </Button>
        </HStack>
      </Flex>
      <Text fontSize="sm" color="#a3a3a3" mb={8}>
        Step {step + 1} of {STEPS.length} — {STEPS[step].label}
      </Text>

      {isLockedForReview && (
        <Box
          p={4}
          mb={8}
          bg={lockStatus === 'approved' ? 'rgba(122,226,161,0.06)' : 'rgba(251,146,60,0.06)'}
          border={`1px solid ${lockStatus === 'approved' ? 'rgba(122,226,161,0.22)' : 'rgba(251,146,60,0.22)'}`}
          borderRadius="10px"
        >
          <HStack spacing={2} mb={1.5}>
            <Box
              color={lockStatus === 'approved' ? 'rgba(122,226,161,0.85)' : 'rgba(251,146,60,0.85)'}
              flexShrink={0}
              display="flex"
              alignItems="center"
            >
              <FiShield size={14} />
            </Box>
            <Text
              fontSize="sm"
              fontWeight="600"
              color={lockStatus === 'approved' ? 'rgba(122,226,161,0.95)' : 'rgba(251,146,60,0.95)'}
            >
              {lockStatus === 'approved' ? 'Approved & locked' : 'Submitted for review. Review in progress.'}
            </Text>
          </HStack>
          <Text fontSize="xs" color="rgba(255,255,255,0.55)" lineHeight="1.7" pl="22px">
            {lockStatus === 'approved'
              ? 'This event is compliance-approved and can no longer be edited.'
              : "Your reviewer is reviewing this event. You can't edit it until they respond with a verdict — that way they sign off on exactly what you submitted."}
          </Text>
        </Box>
      )}

      {/* Rejected → surface the reviewer's note so the advisor knows what to
          fix. Sticky on every step (unlike the lock banner, the event is fully
          editable here). Full text opens in the slide-in drawer below. */}
      {showRejectionNotice && (
        <Box
          p={4}
          mb={8}
          bg="rgba(248,113,113,0.06)"
          border="1px solid rgba(248,113,113,0.22)"
          borderRadius="10px"
        >
          <Flex align="flex-start" justify="space-between" gap={4}>
            <Box flex="1" minW={0}>
              <HStack spacing={2} mb={1.5}>
                <Box color="rgba(248,113,113,0.9)" flexShrink={0} display="flex" alignItems="center">
                  <FiAlertTriangle size={14} />
                </Box>
                <Text fontSize="sm" fontWeight="600" color="rgba(248,113,113,0.95)">
                  Changes requested by your reviewer
                </Text>
              </HStack>
              <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={2} pl="22px">
                {existingEvent?.reviewerName || 'Your reviewer'}
                {existingEvent?.reviewedAt
                  ? ` · ${new Date(existingEvent.reviewedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                  : ''}
              </Text>
              <Text
                fontSize="sm"
                color="rgba(255,255,255,0.75)"
                lineHeight="1.7"
                pl="22px"
                noOfLines={2}
              >
                {reviewerNote}
              </Text>
            </Box>
            <Button
              leftIcon={<FiMessageSquare size={14} />}
              variant="ghost"
              size="sm"
              flexShrink={0}
              color="rgba(248,113,113,0.95)"
              borderRadius="full"
              fontSize="sm"
              border="1px solid rgba(248,113,113,0.3)"
              _hover={{ bg: 'rgba(248,113,113,0.1)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => setShowReviewerNote(true)}
            >
              Read full note
            </Button>
          </Flex>
        </Box>
      )}

      <Stepper current={step} />

      <Box
        opacity={isLockedForReview ? 0.5 : 1}
        pointerEvents={isLockedForReview ? 'none' : 'auto'}
        aria-disabled={isLockedForReview}
      >
        {stepContent}
      </Box>

      {/* Shown on both the Event Details and Compliance steps — reminds the
          advisor that submission triggers a compliance review. Hidden once
          locked (the lock banner above already covers the state). */}
      {(step === STEP_DETAILS || step === STEP_PROMOTION || step === STEP_COMPLIANCE) && !isLockedForReview && (
        <Box
          p={4}
          mt={6}
          bg="rgba(251,191,36,0.06)"
          border="1px solid rgba(251,191,36,0.18)"
          borderRadius="10px"
        >
          <HStack spacing={2} mb={1.5}>
            <Box color="rgba(251,191,36,0.8)" flexShrink={0} display="flex" alignItems="center">
              <FiShield size={14} />
            </Box>
            <Text fontSize="sm" fontWeight="600" color="rgba(251,191,36,0.9)">
              Compliance review required
            </Text>
          </HStack>
          <Text fontSize="xs" color="rgba(255,255,255,0.45)" lineHeight="1.7" pl="22px">
            Once submitted, your compliance team will review the event description and materials. The event date and venue booking will be confirmed only after approval.
          </Text>
        </Box>
      )}

      <Flex align="center" justify="space-between" mt={8}>
        <Button
          variant="ghost"
          size="sm"
          color="#a3a3a3"
          borderRadius="full"
          fontSize="sm"
          _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
          _focus={{ boxShadow: 'none' }}
          onClick={handleSaveDraft}
          isDisabled={isSaving || isLockedForReview}
        >
          Save as draft
        </Button>

        <HStack spacing={2}>
          {step > 0 && (
            <Button
              leftIcon={<FiArrowLeft size={15} />}
              variant="ghost"
              size="sm"
              h="36px"
              color="#fafafa"
              borderRadius="full"
              fontSize="sm"
              border="1px solid rgba(255,255,255,0.12)"
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={goBack}
            >
              Back
            </Button>
          )}
          {!isLastStep ? (
            <Button
              rightIcon={<FiArrowRight size={15} />}
              size="sm"
              h="36px"
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
              onClick={goNext}
            >
              Continue
            </Button>
          ) : (
            <Button
              leftIcon={<FiShield size={14} />}
              size="sm"
              h="36px"
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              _hover={{ opacity: 0.85 }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleSubmitForReview}
              isLoading={isSaving}
              isDisabled={isLockedForReview}
              loadingText="Saving…"
            >
              {isEditing && existingEvent?.complianceStatus ? 'Save & Resubmit' : 'Submit for Review'}
            </Button>
          )}
        </HStack>
      </Flex>

      {/* Slide-in attendee preview */}
      <Box
        position="fixed"
        inset={0}
        zIndex={1400}
        pointerEvents={showPreview ? 'auto' : 'none'}
      >
        <Box
          position="absolute"
          inset={0}
          bg="rgba(0,0,0,0.65)"
          opacity={showPreview ? 1 : 0}
          transition="opacity 0.25s ease"
          onClick={() => setShowPreview(false)}
        />
        <Flex
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          w={{ base: '100%', md: '480px' }}
          bg="#0d0d0d"
          borderLeft="1px solid rgba(255,255,255,0.08)"
          direction="column"
          transform={showPreview ? 'translateX(0)' : 'translateX(100%)'}
          transition="transform 0.3s cubic-bezier(0.4,0,0.2,1)"
          boxShadow="-20px 0 60px rgba(0,0,0,0.5)"
        >
          <Flex align="center" justify="space-between" px={5} py={4} borderBottom="1px solid rgba(255,255,255,0.08)" flexShrink={0}>
            <Button
              leftIcon={<FiArrowLeft size={15} />}
              variant="ghost"
              size="sm"
              color="#fafafa"
              borderRadius="full"
              fontSize="sm"
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => setShowPreview(false)}
            >
              Back to editing
            </Button>
            <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="600" letterSpacing="0.04em" textTransform="uppercase">
              Attendee preview
            </Text>
          </Flex>
          <Box flex="1" overflowY="auto" px={5} py={8} bg="#0d0d0d">
            <EventPreviewCard
              data={{ ...form, venueName: resolvedVenueName, venueType: selectedVenue?.type }}
              mode="review"
            />
            <ComplianceBanner />
            <Text fontSize="xs" color="rgba(255,255,255,0.3)" textAlign="center" mt={5} lineHeight="1.6">
              This is how your event page will look to invitees once compliance approves and you confirm a date.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Slide-in reviewer note — full verbatim text of the rejection note. */}
      <Box
        position="fixed"
        inset={0}
        zIndex={1400}
        pointerEvents={showReviewerNote ? 'auto' : 'none'}
      >
        <Box
          position="absolute"
          inset={0}
          bg="rgba(0,0,0,0.65)"
          opacity={showReviewerNote ? 1 : 0}
          transition="opacity 0.25s ease"
          onClick={() => setShowReviewerNote(false)}
        />
        <Flex
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          w={{ base: '100%', md: '480px' }}
          bg="#0d0d0d"
          borderLeft="1px solid rgba(255,255,255,0.08)"
          direction="column"
          transform={showReviewerNote ? 'translateX(0)' : 'translateX(100%)'}
          transition="transform 0.3s cubic-bezier(0.4,0,0.2,1)"
          boxShadow="-20px 0 60px rgba(0,0,0,0.5)"
        >
          <Flex align="center" justify="space-between" px={5} py={4} borderBottom="1px solid rgba(255,255,255,0.08)" flexShrink={0}>
            <Button
              leftIcon={<FiArrowLeft size={15} />}
              variant="ghost"
              size="sm"
              color="#fafafa"
              borderRadius="full"
              fontSize="sm"
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={() => setShowReviewerNote(false)}
            >
              Back to editing
            </Button>
            <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="600" letterSpacing="0.04em" textTransform="uppercase">
              Reviewer note
            </Text>
          </Flex>
          <Box flex="1" overflowY="auto" px={5} py={8} bg="#0d0d0d">
            <HStack spacing={2} mb={1.5}>
              <Box color="rgba(248,113,113,0.9)" flexShrink={0} display="flex" alignItems="center">
                <FiAlertTriangle size={15} />
              </Box>
              <Text fontSize="md" fontWeight="700" color="#fafafa">
                Changes requested
              </Text>
            </HStack>
            <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={5} pl="23px">
              {existingEvent?.reviewerName || 'Your reviewer'}
              {existingEvent?.reviewedAt
                ? ` · ${new Date(existingEvent.reviewedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                : ''}
            </Text>
            <Box
              p={4}
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.08)"
              borderRadius="10px"
            >
              <Text fontSize="sm" color="rgba(255,255,255,0.85)" lineHeight="1.8" whiteSpace="pre-wrap">
                {reviewerNote}
              </Text>
            </Box>
            <Text fontSize="xs" color="rgba(255,255,255,0.3)" mt={5} lineHeight="1.6">
              Edit your event to address this feedback, then resubmit for review.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Submit → invite a compliance reviewer (the "send for review" step). */}
      <EventComplianceModal
        isOpen={Boolean(submittedEventId)}
        eventId={submittedEventId}
        eventTitle={form.title.trim()}
        onClose={() => {
          setSubmittedEventId(null);
          navigate('/?view=events');
        }}
      />

      {/* Reopen-for-edit confirmation. Reopening is destructive to the active
          review (it kills the reviewer's link), so it's gated behind an explicit
          confirm that spells out the consequence. */}
      <Modal
        isOpen={showReopenConfirm}
        onClose={() => !reopenMutation.isLoading && setShowReopenConfirm(false)}
        isCentered
        size="md"
      >
        <ModalOverlay bg="rgba(0,0,0,0.85)" backdropFilter="blur(3px)" />
        <ModalContent bg="#0f0f0f" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px" color="#fafafa" mx={4}>
          <ModalHeader pb={2}>
            <HStack spacing={2.5}>
              <Box
                color="rgba(251,146,60,0.9)"
                p={1.5}
                borderRadius="8px"
                bg="rgba(251,146,60,0.1)"
                border="1px solid rgba(251,146,60,0.25)"
                display="flex"
                alignItems="center"
              >
                <FiEdit2 size={15} />
              </Box>
              <Text fontSize="md" fontWeight="600">Edit this event?</Text>
            </HStack>
          </ModalHeader>
          <ModalBody pb={4}>
            <Text fontSize="sm" color="rgba(255,255,255,0.7)" lineHeight="1.7">
              This event is currently with your reviewer. Editing reopens it as a
              draft and we&apos;ll email your reviewer that their current link no
              longer works — so they can&apos;t act on the old version. When
              you&apos;re done, resubmit for review to send a fresh link (to the
              same reviewer or a new one).
            </Text>
          </ModalBody>
          <ModalFooter borderTop="1px solid rgba(255,255,255,0.08)" gap={2}>
            <Button
              size="sm"
              variant="ghost"
              color="rgba(255,255,255,0.5)"
              onClick={() => setShowReopenConfirm(false)}
              isDisabled={reopenMutation.isLoading}
              borderRadius="full"
              _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            >
              Keep under review
            </Button>
            <Button
              size="sm"
              bg="rgba(251,146,60,0.95)"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              px={4}
              isLoading={reopenMutation.isLoading}
              loadingText="Reopening…"
              onClick={handleReopenForEdit}
              _hover={{ opacity: 0.88 }}
              _focus={{ boxShadow: 'none' }}
            >
              Edit &amp; notify reviewer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CreateEventWizard;
